import { MediaPlayerTime } from './../models/MediaPlayerTime';
import { clamp, get } from 'lodash';
import { all, fork, select, takeEvery, put } from 'typed-redux-saga/macro';

import {
  JUMP_MEDIA,
  PAUSE_MEDIA,
  PLAY_MEDIA,
  PLAY_MEDIA_BACKWARDS,
  PLAYBACK_RATE_MEDIA,
  SEEK_MEDIA,
  SEEK_PLAY_MEDIA,
  TOGGLE_PLAY_PAUSE_MEDIA,
  PLAYER_PAUSE,
  PLAYER_PLAY,
  PLAYER_SEEKED,
  PlaybackRatePayload,
  JumpMediaPayload,
  SEEK_PREV_MARKER_TIME,
  actionSetPrevMarkerTime,
  setIsPlayerSeeked,
  actionAutoEngageOnscreenOnlyFilter,
} from '../actions';
import {
  selectMediaDuration,
  selectFrameRate,
  selectContentTypeHasVideo,
  selectOnscreenOnlyFilter,
  selectOnscreenOnlyFilterUserSet,
} from '../selectors';
import { selectFeatureFlags } from '@user-permissions';

import {
  findFrameIndex,
  convertFrameIndexToTimeMs,
  frameAlignStartTimeMs,
} from '@utils';

interface Action<P> {
  type: string;
  payload: P;
}

function* askPlayer<R = void>(
  fn: (mp: NonNullable<Window['mediaPlayer']>) => R
) {
  yield window.mediaPlayer && fn(window.mediaPlayer);
}

let isTemporaryPlayerPause = false; // set true when we need to pause player temporarily
let disableSeekedAlignment = false; // sometimes we want to disable this behavior (such as when using the backwards emulator)

export function computeSeekTime(timeMs: number) {
  // convert timeMs to a safe seek position

  // round up to whole millisecond position (avoids rounding down into previous frame)
  // time steps such as 66.6667 ms will round up to 67 ms so displays as .067 instead of 0.066 on the playhead

  // also add a small epsilon to ensure inside of frame (some time step have floating point issues
  // ex. seeking to 8.2 goes to 8.1999999 which then rounds down to previous frame during alignment)
  return Math.ceil(timeMs) / 1000.0 + 0.0001;
}

export function* videoSaga() {
  yield* all([
    fork(onActionSeekVideo),
    fork(onActionSeekPlayVideo),
    fork(onActionPlayVideo),
    fork(onActionPlayVideoBackwards),
    fork(onActionPauseVideo),
    fork(onActionTogglePlayPauseVideo),
    fork(onActionPlaybackRateVideo),
    fork(onActionJumpVideo),
    fork(onActionPlayerPause),
    fork(onPlayerPlay),
    fork(onPlayerSeeked),
    fork(onActionSeekPrevMarkerTime),
  ]);
}

export function* onActionSeekVideoHandle({
  payload,
}: {
  payload: MediaPlayerTime;
}) {
  const duration = yield* select(selectMediaDuration);
  const fps = yield* select(selectFrameRate);

  let startTimeMs = payload.startTimeMs;

  // align with frame start time
  if (fps) {
    startTimeMs = frameAlignStartTimeMs(startTimeMs, fps); // assume always seek to the start time of a frame
  }

  yield askPlayer((mediaPlayer) => {
    let seekTime = computeSeekTime(startTimeMs);

    // if seek to a time that is the same frame (using click and drag of playhead) then playhead will not re-render to aligned position
    // force re-render by adding epsilon to currentTime - will break the equality test in mediaPlayerView lastCurrentTimeReported == currentTime
    if (
      fps &&
      Math.abs(mediaPlayer.getState().player.currentTime - seekTime) < 0.001
    ) {
      seekTime += 0.0001; // add a tenth of ms
    }

    seekTime = clamp(seekTime, 0, duration - 0.000005); // hack for instability around end of video)

    mediaPlayer.seek(seekTime);
  });
}

export function* onActionSeekVideo() {
  // seek to new position clicking on playhead timeline(from pause or playing state), or seek to cluster/group start time
  yield* takeEvery(SEEK_MEDIA, onActionSeekVideoHandle);
}

export function* onActionSeekPlayVideoHandle({
  payload,
}: {
  payload: MediaPlayerTime;
}) {
  const duration = yield* select(selectMediaDuration);
  yield askPlayer((mediaPlayer) => {
    const destination = clamp(
      computeSeekTime(payload.startTimeMs),
      0,
      duration - 0.000005 // hack for instability around end of video
    );

    mediaPlayer.seek(destination);
    if (get(window, 'mediaPlayer.props.hasStarted')) {
      mediaPlayer.play();
    }
  });
}

export function* onActionSeekPlayVideo() {
  // how do we get here?
  yield* takeEvery(SEEK_PLAY_MEDIA, onActionSeekPlayVideoHandle);
}
export function* onActionPlayVideoHandle() {
  yield askPlayer((mediaPlayer) => {
    mediaPlayer.play();
  });
}

export function* onActionPlayVideo() {
  // right arrow held down
  yield* takeEvery(PLAY_MEDIA, onActionPlayVideoHandle);
}

let backwardsPlayEmulation: number;

export function* onActionPlayVideoBackwardsHandle() {
  const fps = yield* select(selectFrameRate);
  disableSeekedAlignment = true;
  yield askPlayer((mediaPlayer) => {
    mediaPlayer.pause(); // make sure is paused first so doesn't fight
    try {
      // this probably won't work on most players
      mediaPlayer.playbackRate = -Math.abs(mediaPlayer.playbackRate);
      mediaPlayer.play();
    } catch (_error) {
      // let cumTimeBackwords = 0;
      let windowIntervalMs = 20;
      if (fps) {
        windowIntervalMs = 1000 / fps;
      }
      backwardsPlayEmulation = window.setInterval(() => {
        // option to limit max backwards playback - no longer needed
        // if (cumTimeBackwords > 5000) {
        //   // force end after 5 seconds since currently cannot stop it
        //   return clearInterval(backwardsPlayEmulation);
        // }
        if (!window.mediaPlayer) {
          return clearInterval(backwardsPlayEmulation);
        }
        // cumTimeBackwords += windowIntervalMs;

        window.mediaPlayer.forward(-windowIntervalMs / 1000);
      }, windowIntervalMs);
    }
  });
}

export function* onActionPlayVideoBackwards() {
  yield* takeEvery(PLAY_MEDIA_BACKWARDS, onActionPlayVideoBackwardsHandle);
}

export function* onActionPauseVideoHandle() {
  yield askPlayer((mediaPlayer) => {
    clearInterval(backwardsPlayEmulation);
    disableSeekedAlignment = false;
    mediaPlayer.playbackRate = Math.abs(mediaPlayer.playbackRate) || 1.0;
    mediaPlayer.pause();
  });
}

export function* onActionPauseVideo() {
  // pause mediaPlayer when release arrow key being held down
  yield* takeEvery(PAUSE_MEDIA, onActionPauseVideoHandle);
}

export function* onActionTogglePlayPauseVideoHandle() {
  yield askPlayer((mediaPlayer) => {
    if (get(mediaPlayer, 'video.video.paused')) {
      mediaPlayer.play();
    } else {
      mediaPlayer.pause();
    }
  });
}

export function* onActionTogglePlayPauseVideo() {
  // pause/play mediaPlayer using spacebar
  yield* takeEvery(TOGGLE_PLAY_PAUSE_MEDIA, onActionTogglePlayPauseVideoHandle);
}

/**
 * Engage the playhead scope the first time a TDO is played (VE-25876).
 *
 * The full cluster list is not scannable while video runs — by the time the eye
 * has found a card the frame has moved on — so the scope the operator would
 * want is applied for them, once, at the moment they start reviewing footage.
 *
 * Once only, and only until they say otherwise. `onscreenOnlyFilterUserSet`
 * records that they have decided either way, and nothing here fires again after
 * that: an operator who turns the scope off is not fighting it back off at every
 * play. Play is the sole trigger, deliberately — seeking would arm it too, but
 * clicking a cluster card seeks, so inspecting one card would collapse the list
 * as a side effect.
 *
 * Not audited, and nor is the toggle: this is a view filter, like the type
 * filter beside it, and neither changes what gets redacted. It still goes
 * through its own action rather than SET_ONSCREEN_ONLY_FILTER, because that one
 * records that the operator decided — which is what stops this firing again.
 *
 * `autoEngageOnScreenFilter` (default on) is the kill switch for THIS saga and
 * nothing else: with it off the toggle stays in the panel and keeps working,
 * operators just engage the scope themselves. See `user-permissions/selectors`.
 */
export function* onPlayerPlayHandle() {
  const { autoEngageOnScreenFilter } = yield* select(selectFeatureFlags);
  if (!autoEngageOnScreenFilter) {
    return;
  }

  const userSet = yield* select(selectOnscreenOnlyFilterUserSet);
  const alreadyEngaged = yield* select(selectOnscreenOnlyFilter);
  // Video only. The scope is meaningless without a moving playhead, and the
  // panel that offers it is not reachable otherwise: it is withheld for images,
  // and for audio-only media the Faces tab is disabled outright. Asking for
  // video rather than excluding images covers both in one test, and does not
  // quietly admit a third content type if one is ever added.
  const hasVideo = yield* select(selectContentTypeHasVideo);

  if (userSet || alreadyEngaged || !hasVideo) {
    return;
  }

  yield* put(actionAutoEngageOnscreenOnlyFilter());
}

export function* onPlayerPlay() {
  // `takeEvery` rather than a one-shot take: the flag it guards resets with the
  // slice when another TDO is opened, so this has to re-arm per TDO. Every play
  // after the first costs three selects and a return.
  yield* takeEvery(PLAYER_PLAY, onPlayerPlayHandle);
}

export function* onActionPlayerPause() {
  // player has been paused
  yield* takeEvery<Action<void> & { videoProps: { currentTime: number } }>(
    PLAYER_PAUSE,
    function* (action) {
      const { currentTime } = action.videoProps;
      const fps = yield* select(selectFrameRate);
      const duration = yield* select(selectMediaDuration);

      // don't align to frame start at the end of video - this way play action will seek back to the beginning
      if (fps && duration - currentTime < 1 / fps) {
        return;
      }

      yield askPlayer((mediaPlayer) => {
        let seekTime = currentTime;

        // align with frame start time
        if (fps) {
          const frameIndex = findFrameIndex(currentTime * 1000, fps);

          // grabbing currentTime from action.videoProps appear to have fixed issue workaround below was addressing
          // -------------------
          // sometimes video is one frame ahead of mediaPlayer.props.currentTime - causes endtime alignment errors on UDRs
          // seek to next frame to ensure alignment of frame, cursor, udr end and avoid player "rewind"
          // if close to frame boundary move to next frame (within 25 ms)
          // const nextFrameTime =
          //   convertFrameIndexToTimeMs(frameIndex + 1, fps) / 1000.0;

          // if (nextFrameTime - currentTime < 0.025) {
          //   seekTime = nextFrameTime;
          // } else {
          //   seekTime = convertFrameIndexToTimeMs(frameIndex, fps) / 1000.0;
          // }
          // ----------------

          seekTime = convertFrameIndexToTimeMs(frameIndex, fps) / 1000.0;
          // currentTime = convertFrameIndexToTimeMs(frameIndex + 0.5, fps)/ 1000.0; // reproduce the lagging video bug GLC-4661 by pausing in between frames
          seekTime = computeSeekTime(seekTime * 1000);
          if (seekTime < duration) {
            mediaPlayer.seek(seekTime);
          }
        }
      });
    }
  );
}

export function* onActionPlaybackRateVideoHandle({
  payload,
}: {
  payload: PlaybackRatePayload;
}) {
  yield askPlayer((mediaPlayer) => {
    mediaPlayer.playbackRate = payload.playbackRate;
  });
}

export function* onActionPlaybackRateVideo() {
  yield* takeEvery(PLAYBACK_RATE_MEDIA, onActionPlaybackRateVideoHandle);
}

export function* onActionSeekPrevMarkerTime() {
  yield* takeEvery(SEEK_PREV_MARKER_TIME, function* ({ payload }) {
    const fps = yield* select(selectFrameRate);

    let prevMarkerMs = payload;

    // align with frame start time
    if (fps) {
      prevMarkerMs = frameAlignStartTimeMs(prevMarkerMs, fps); // assume always seek to the start time of a frame
    }

    yield* put(actionSetPrevMarkerTime(prevMarkerMs));
  });
}

export function* onPlayerSeekedHandle(
  action: Action<void> & {
    videoProps: {
      currentTime: number;
    };
  }
) {
  const { currentTime } = action.videoProps;
  if (!currentTime) {
    return;
  }
  if (disableSeekedAlignment) {
    return;
  }

  yield* put(setIsPlayerSeeked(true));
  const currentTimeMs = currentTime * 1000;
  const fps = yield* select(selectFrameRate);

  // if seeking was done on the player instead of timeline then we haven't aligned with the frame yet
  if (fps) {
    // find seek time that aligns with frame start time
    const startTimeMs = frameAlignStartTimeMs(currentTimeMs, fps);
    const seekTime = computeSeekTime(startTimeMs);

    // check if video is currently paused
    let isPaused = false;
    yield askPlayer((mediaPlayer) => {
      isPaused = !!get(mediaPlayer, 'video.video.paused');
    });
    // check if too far from desired seekTime (allow 1e-5 tol to ignore floating point issues)
    if (Math.abs(seekTime - currentTime) > 0.00001) {
      if (isPaused) {
        // safe to align with frame start when paused
        yield askPlayer((mediaPlayer) => {
          mediaPlayer.seek(seekTime);
        });
      } else {
        // if video is playing need to pause temporarily to avoid infinite loop
        isTemporaryPlayerPause = true;
        yield askPlayer((mediaPlayer) => {
          mediaPlayer.pause(); // inside onActionPlayerPause we seek (so return to this function a second time to resume playing)
        });
      }
    } else {
      // check if we need to release the player hold (resume playing)
      if (isPaused && isTemporaryPlayerPause) {
        isTemporaryPlayerPause = false; // release temporary pause
        yield askPlayer((mediaPlayer) => {
          mediaPlayer.play();
        });
      }
    }
  }
}

export function* onPlayerSeeked() {
  // seek via player control (also seek from timeline goes through this)
  yield* takeEvery<Action<void> & { videoProps: { currentTime: number } }>(
    PLAYER_SEEKED,
    onPlayerSeekedHandle
  );
}

export function* onActionJumpVideoHandle({
  payload,
}: {
  payload: JumpMediaPayload;
}) {
  const fps = yield* select(selectFrameRate);
  yield askPlayer((mediaPlayer) => {
    const currentTime = mediaPlayer.getState().player.currentTime;
    let jumpTimeMs;

    // jump by seconds
    if (payload.seconds) {
      jumpTimeMs = 1000 * (currentTime + payload.seconds);

      // if fps align with frame start
      if (fps) {
        jumpTimeMs = frameAlignStartTimeMs(jumpTimeMs, fps);
      }
    } else {
      if (payload.frames !== undefined) {
        // jump by frames
        if (fps) {
          const frameIndex = findFrameIndex(currentTime * 1000, fps);
          jumpTimeMs = convertFrameIndexToTimeMs(
            frameIndex + payload.frames,
            fps
          );
        } else {
          //  legacy videos / and audio only don't have frameRate - assume default 30 fps
          // todo: consider having a default audio only step size 200 ms?
          jumpTimeMs = 1000 * currentTime + (1000 * payload.frames) / 30;
        }
      } else {
        return;
      }
    }

    mediaPlayer.forward(computeSeekTime(jumpTimeMs) - currentTime); //  calculate precise shift that moves to desired seekTime
  });
}

export function* onActionJumpVideo() {
  // step via left/right arrow keys
  yield* takeEvery(JUMP_MEDIA, onActionJumpVideoHandle);
}
